/**
 * `@kitup/brand-talearoo/legal` — the web-only half of the brand.
 *
 * Separate from the root export because everything here is JSX, and the API
 * imports the root. See `LegalConfig` in `@kitup/brand`.
 */
import type { LegalConfig } from '@kitup/brand';
import { talearooDocuments } from './documents.js';
import { talearooLegal } from './legal-data.js';

export { talearooLegal, talearooDocuments };

const { reseller, support, privacy, abuse } = talearooLegal;

/**
 * TaleAroo's legal bundle — a web-only export, for the same reason as MyLore's:
 * the documents are JSX, and the API imports the brand.
 */
export const legal: LegalConfig = {
  indexTitle: 'Legal information',
  indexIntro: () => (
    <p>
      {talearooLegal.serviceName} turns photographs of your child into an illustrated personalised
      book, with optional animated scenes and spoken narration. The illustrations are generated by
      artificial intelligence, which means the photographs you upload are sent to services outside
      Canada. What is sent, to whom, and for how long it is kept is set out below.
    </p>
  ),
  quarantineTerm: talearooLegal.quarantineTerm,
  documentVersion: talearooLegal.documentVersion,
  documentVersionId: talearooLegal.documentVersionId,
  indexOutroTitle: 'How the set is organized',
  indexOutro: () => (
    <>
      <p>
        The three consents are separate documents, and they are separate acts: accepting the Terms
        of Service does not give us permission to process a child’s photographs, agreeing to the
        photographs does not cover a voice recording, and neither of them opens the book to
        strangers. Each is asked for on its own, next to the checkbox that grants it, and each can
        be withdrawn without touching the others.
      </p>
      <p>
        Each document is named for what it does, so you can tell from the title which one governs
        what: the agreement you enter into, the notice of what we collect, the terms of payment, and
        a consent for each kind of material you upload.
      </p>
    </>
  ),
  requisitesTitle: 'Service and seller',
  requisites: [
    { label: 'Service', value: talearooLegal.serviceName },
    { label: 'Business', value: `${talearooLegal.businessName}, British Columbia, Canada` },
    { label: 'Website', value: 'talearoo.com', href: 'https://talearoo.com' },
    { label: 'Seller of record', value: reseller.name },
    { label: 'Support', value: support.email, href: `mailto:${support.email}` },
    { label: 'Privacy', value: privacy.email, href: `mailto:${privacy.email}` },
    { label: 'Abuse reports', value: abuse.email, href: `mailto:${abuse.email}` },
  ],
  documents: talearooDocuments,
  footerContacts: [
    { icon: 'instagram', ...talearooLegal.socials.instagram },
    { icon: 'facebook', ...talearooLegal.socials.facebook },
    { icon: 'tiktok', ...talearooLegal.socials.tiktok },
    { icon: 'youtube', ...talearooLegal.socials.youtube },
  ],
  footerLine: `${talearooLegal.businessName} · British Columbia, Canada`,
  documentLinks: {
    registerTerms: { en: 'terms of service' },
    paywallTerms: { en: 'terms of service' },
    paywallRefund: { en: 'payment and refund policy' },
    pricingRules: { en: 'payment and refund policy' },
  },
  /*
   * The two consents are documents of their own, not a paragraph in the Terms:
   * sensitive data needs express consent given in a separate act, and a
   * biometric voiceprint needs a notice that names what is collected, why and
   * for how long. The checkboxes in PhotoConsent and NarrationStep link here,
   * so what a person reads before ticking is exactly what they are granting.
   */
  routes: {
    terms: '/legal/terms',
    privacy: '/legal/privacy',
    payment: '/legal/payment',
    photoConsent: '/legal/photo-consent',
    voiceConsent: '/legal/voice-consent',
    publicationConsent: '/legal/publication-consent',
  },
  footerLinks: [
    { label: 'Pricing', to: '/pricing' },
    { label: 'Terms', to: '/legal/terms' },
    { label: 'Privacy', to: '/legal/privacy' },
    { label: 'Payment and refunds', to: '/legal/payment' },
    { label: 'Legal', to: '/legal' },
  ],
  ui: {
    backHome: '← Back to home',
    allDocuments: 'All legal documents',
    documentsTitle: 'Documents',
    editionPrefix: 'Edition of',
    versionPrefix: 'version',
    footerNavLabel: 'Service information',
  },
};
